/**
 * A PO line has ONE status, and this is where it is decided.
 *
 * Aaron, 17 Sep 2026, walking PR 2 — the ruling this module exists for:
 *
 *   *"we need to stop thinking of it as an underlying status. The status IS On
 *   Hold (Customer/Supplier). It cannot be both on hold and in motion... that
 *   makes it logically consistent and fixes some of these issues we have in the
 *   UI where we have multiple badges contradicting themselves."*
 *
 * He is describing a class of defect, not an instance. "On hold" and "changed"
 * were two truths a line could carry at once, so every surface had to pick one
 * to show and each picked differently: the PO Lines grid substituted On Hold
 * into the status cell, the schedule grid did not, the line modal showed a
 * status badge and a separate On Hold chip side by side, and the header's
 * "Waiting on…" badge answered from the acknowledge status while the grid's
 * answered from the hold. Four surfaces, four answers, one line.
 *
 * So: one function, asked by every surface, and no surface reads
 * `acknowledge_status` for display again. A held line's status is the hold —
 * full stop, no second badge, no fallback shown underneath.
 *
 * "EVERY SURFACE" IS A CLAIM, AND IT WAS FALSE WHEN IT WAS FIRST WRITTEN (F6,
 * gate round 1): the schedule modal, the line modal's schedule tab and the
 * printable PO summary each still had a status table of their own — six tables
 * in all — and two of them rendered a separate On Hold column beside the badge,
 * which is the exact contradiction above. They ask this module now. The claim
 * is about DISPLAY and only display: `pendingProposal`, `changeRequestGrouping`
 * and the tabs' action gates read `acknowledge_status` as the negotiation STATE,
 * which is what it is for, and a hold never overwrites it.
 *
 * The same sentence covers the hold's other question — who may take it off.
 * `mayReleaseHold` below is the one answer to that, and it mirrors the server
 * clause for clause.
 *
 * WHAT IS NOT LOST, and the distinction matters: the line still has to know
 * what it returns to when the hold lifts. That is a resume point in the DATA
 * (`previous_acknowledge_status`, and `acknowledge_status` itself, neither of
 * which the hold touches) — never something the UI shows. Releasing a hold puts
 * the line back exactly where it was, which is the behaviour the 16 Sep split
 * was protecting; this keeps that and drops the second column that was paying
 * for it.
 *
 * NOT done here, deliberately: writing 'on_hold' into `acknowledge_status`
 * itself. That is the deferred status collapse in the scope ledger, it needs
 * the change request's open state to live off the line first, and it would make
 * the hold's own columns (reason, placer, nominee) a second owner of a fact this
 * one field would then also carry.
 */

/** A badge: what it says and how it looks. */
export interface StatusConfig {
  label: string;
  className: string;
}

/** A status ready to render, with the hover text where there is one to give. */
export interface StatusDisplay extends StatusConfig {
  title?: string;
}

/**
 * The acknowledge statuses, which are the line's status whenever it is not held.
 *
 * THE one copy. There were four: this table, plus near-identical maps in
 * PODetailHelpers, POLineDetailTab and POLineHeaderBadges that disagreed about
 * whether `accepted` reads "WIP" or "Accepted" and whether a proposed change
 * reads "Changed", "Change Proposed (S)" or "Changes Pending" — so the same
 * line was three different words on three screens.
 */
export const ACKNOWLEDGE_STATUS_CONFIG: Record<string, StatusConfig> = {
  new: { label: 'New', className: 'bg-blue-500 text-white' },
  declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
  change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
  change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
  wip: { label: 'WIP', className: 'bg-green-500 text-white' },
  accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
  despatched: { label: 'Dispatched', className: 'bg-gray-200 text-gray-900' },
  change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  closed: { label: 'Closed', className: 'bg-gray-400 text-white' },
};

/** Everything the status depends on. Both grains carry these names. */
export interface LineStatusFields {
  /**
   * The RECEIPT state — the quantity projection, not the negotiation.
   *
   * CR-7412: this interface could not express the question at all, which is how
   * the defect got through. PR 2 collapsed the PO Lines status cell onto the
   * acknowledge status alone, and on a tenant whose lines were never acknowledged
   * every fully received line went back to reading "New". Amber reproduced it on
   * Gardner and AC the morning it merged.
   */
  status?: string | null;
  acknowledge_status?: string | null;
  on_hold?: boolean;
  on_hold_by_company_type?: string | null;
  /** G7: the side the placer nominated, and the only side that may release. */
  hold_next_action_with?: string | null;
  on_hold_reason_text?: string | null;
}

/**
 * A side of the table, spelled as the server's `_side()` spells it. Four
 * values, because the rule answers a four-valued question: `customer` and
 * `supplier` are the two sides, `helix` is the operator, and `logistics` is the
 * member neither branch names — and both of the last two can place a hold.
 */
export type HoldSide = 'customer' | 'supplier' | 'helix' | 'logistics';

const HOLD_SIDES: readonly string[] = ['customer', 'supplier', 'helix', 'logistics'];

/** What each side is called on a badge, and inside a sentence. */
const HOLD_SIDE_WORDS: Record<HoldSide, { badge: string; sentence: string }> = {
  customer: { badge: 'Customer', sentence: 'the customer' },
  supplier: { badge: 'Supplier', sentence: 'the supplier' },
  helix: { badge: 'Helix', sentence: 'Helix support' },
  logistics: { badge: 'Logistics', sentence: 'logistics' },
};

/**
 * The side a hold sits with — the nominee, falling back to whoever placed it on
 * holds taken before the dialog asked. Matches `mayReleaseHold` and the
 * server's `release_hold`, so the badge names the side that can actually act.
 *
 * It used to collapse anything that was not `supplier` into `customer`, so a
 * hold placed by a Helix operator or by a logistics user read "On Hold
 * (Customer)" on every screen — naming a side that the server will not let
 * release it, to a customer who then cannot make the button work (S1, gate
 * round 1).
 *
 * @param line - The line or schedule row.
 * @returns The side named on the hold; `customer` when nothing is recorded.
 */
export function holdSideOf(line: LineStatusFields): HoldSide | null {
  const side = (line.hold_next_action_with || line.on_hold_by_company_type || '').toLowerCase();
  return (HOLD_SIDES.includes(side) ? side : null) as HoldSide | null;
}

/**
 * The viewer's own side, as the server's `_side()` answers it.
 *
 * `company_type` is the same four-valued field the server uses and is the
 * answer whenever it is there; `platform` is the portal the user is in and
 * stands in for older payloads that carry only that.
 */
export function viewerSideOf(
  user?: { company_type?: string | null; platform?: string | null } | null,
  actingAs?: HoldSide | null,
): HoldSide | null {
  // The server answers this from `get_effective_company_type`, which follows the
  // tenant's portal toggle — so on a company that runs BOTH portals the raw company
  // type is not the side the server will judge by (attacker, round 2). Callers that
  // know which portal the user is acting in pass it; the rest fall back to the
  // profile, which is right for every single-portal tenant.
  const type = (user?.company_type || '').toLowerCase();
  const platform = (user?.platform || '').toUpperCase();
  const profile: HoldSide | null =
    HOLD_SIDES.includes(type) ? (type as HoldSide)
      : platform === 'SUPPLIER' ? 'supplier'
      : platform === 'HELIX' ? 'helix'
      : platform === 'CUSTOMER' ? 'customer'
      : null;
  // The OPERATOR and a logistics member are never a trading side, in any portal:
  // the acting side answers "which side of the deal", and they are on neither, so
  // the profile wins. Taking `actingAs` first made the grid ask an operator which
  // side to hand a hold to (attacker chain 3 and blind 3, round 3, reproduced).
  if (profile === 'helix' || profile === 'logistics') return profile;
  return actingAs || profile;
}

/**
 * The side a viewer PLACES a hold as, when they are a side of the deal at all.
 *
 * A platform operator or a logistics member is neither, and has no side to hand a
 * hold to: the dialog does not ask, nothing is nominated, and their hold stays
 * theirs (F-J, blind stage 2, round 2).
 */
export function placerSideOf(viewer: HoldSide | null): 'customer' | 'supplier' | undefined {
  return viewer === 'customer' || viewer === 'supplier' ? viewer : undefined;
}

/**
 * May this viewer take the line off hold? THE rule, for every surface.
 *
 * Mirrors `POLineWorkflowService.release_hold` clause for clause, because this
 * gate is cosmetic and the server's is real: they have to agree or the screen
 * either offers a button that 403s or hides one that would have worked. Both
 * happened — there were three copies of this rule on the frontend and none of
 * them was the server's (C1 and S1, gate round 1, and the blind pass's F20):
 *
 *   - the PO-line grid's read `helix` as customer-side, so it hid Release from
 *     the operator on a supplier's hold, and offered it to a customer on a hold
 *     the operator had placed;
 *   - the schedule grid's and the schedule modal's knew nothing about G7's
 *     nominee at all, so they offered Release to the placer after the hold had
 *     been handed to the other side.
 *
 * The three clauses, in the server's order: the operator may always release; a
 * row with no recorded placer stays releasable by either side, so a hold from
 * before any of this can never become permanently stuck; otherwise the side the
 * hold NAMES is the only side that releases it.
 *
 * @param viewer - The viewer's side; null (an unknown viewer) defers to the
 *   server, which is the behaviour every caller had before this existed.
 * @param line - The line or schedule row.
 */
export function mayReleaseHold(viewer: HoldSide | null, line: LineStatusFields): boolean {
  if (!viewer) return true;
  if (viewer === 'helix') return true;
  const placedBy = (line.on_hold_by_company_type || '').toLowerCase();
  if (!placedBy) return true;
  return viewer === holdSideOf(line);
}

/**
 * The receipt statuses that mean "finished" AT THE PO-LINE GRAIN (CR-7412).
 *
 * `purchasing.closure_sources.CLOSED_LINE_STATUSES` is the server's copy of this
 * list, and `tests/purchasing/test_closed_status_contract.py` fails if the two
 * drift — the boundary is frontend-to-backend, so P2's contract-test clause
 * applies rather than a shared function.
 */
export const PO_LINE_CLOSED_RECEIPT_STATUSES: readonly string[] = ['received', 'cancelled'];

/**
 * The SCHEDULE grain opts OUT, and the reason is not symmetry.
 *
 * A schedule row's `status` speaks a different vocabulary — `scheduled`,
 * `active`, `partially_shipped`, `shipped`, `missed`, `cancelled` — so the
 * PO-line list would never match it anyway. More to the point it does not NEED
 * the level: the schedule grain's acknowledge status is already truthful about
 * closure, because its importer writes `closed` and `missed` into it
 * (`import_service/services/customers/dennis_eagle/schedule_snapshot_reconcile.py`),
 * which is exactly what the PO-line grain lacked. Switching it on would flip a
 * shipped-but-renegotiated row from "Changed (Supplier)" to "Closed" — a
 * behaviour change on a grain with no reported defect, no ruling and no shots.
 * Named here so the omission reads as a decision rather than an oversight, and
 * tested at both grains (P2: a shared choke point names the grain it applies to).
 */
export const SCHEDULE_CLOSED_RECEIPT_STATUSES: readonly string[] = [];

/** Which entry of the grain's own vocabulary a closed receipt renders as. */
const RECEIPT_STATUS_AS_ACK: Record<string, string> = {
  received: 'closed',
  cancelled: 'cancelled',
};

/**
 * The line's status: one answer, for every surface.
 *
 * Three levels, in this order: the RECEIPT state, then the HOLD, then the
 * acknowledge status. Legacy's own order (`OpenOrdersSQL.cs:187`); the reasoning
 * for Closed sitting above the hold is in the body.
 *
 * The HOLD half is shared by both grains, which is the whole point — a held
 * schedule row and a held PO line say the same words. The other two halves are
 * the grain's own, passed in.
 *
 * CORRECTED 18 Sep 2026 (CR-7412). This paragraph used to say the schedule grain
 * keeps `closed` and `missed` because they "are ERP facts a PO line has no
 * equivalent for", adding that "a schedule the ERP never acknowledges sits at
 * `new` for ever, so those two have to stay". That last clause is a description
 * of THIS defect, written one level up and then not applied here: a PO LINE the
 * ERP never acknowledges sat at `new` for ever too, and once received it read
 * "New" on every surface. The PO line's equivalent always existed — it is
 * `status`, the quantity projection — and it simply was not asked.
 *
 * @param line - The PO line or schedule row.
 * @param config - The acknowledge-status vocabulary for this grain. Defaults to
 *   the PO line's.
 * @param closedReceiptStatuses - The `status` values that mean "finished" at this
 *   grain. Defaults to the PO line's; the schedule grain passes
 *   `SCHEDULE_CLOSED_RECEIPT_STATUSES`, which is empty on purpose — see its own
 *   comment for why that is a decision and not an omission.
 * @returns The label, its badge classes, and hover text where there is any.
 */
export function statusOf(
  line: LineStatusFields,
  config: Record<string, StatusConfig> = ACKNOWLEDGE_STATUS_CONFIG,
  closedReceiptStatuses: readonly string[] = PO_LINE_CLOSED_RECEIPT_STATUSES,
): StatusDisplay {
  // CR-7412, LEVEL 1 — a finished line says so, and it says so ABOVE the hold.
  //
  // Aaron, 18 Sep 2026: *"we need an actual Closed po lines status"*, and Closed
  // outranks On Hold. That order is legacy's, not ours: its single status column
  // is a four-level CASE (`OpenOrdersSQL.cs:187`) —
  //
  //     WHEN POL.Completed = 1 THEN 'Close'
  //     WHEN POL.OnHold = 1    THEN 'On Hold'
  //     WHEN <outside firm window> THEN 'Forecast'
  //     WHEN AcknowledgeStatus = 0 THEN 'New' ...
  //
  // and legacy's other rendering of the same bit is `case when POL.Completed = 0
  // THEN 'Open' ELSE 'Closed'` (`NetworkSQL.cs:6886`). We had levels 2 and 4.
  //
  // Why Closed beats the hold, when 17 Sep ruled the hold beats everything: the
  // hold wins because it says whose move it is and the line is still IN MOTION —
  // there is a next action and a resume point to protect. A received line has
  // neither. Nothing is owed, nobody's move is next, and the hold is stale
  // metadata on a finished row. Level 3 (Forecast) is deliberately NOT restored
  // here — it is declared out of scope in the ledger and is Aaron's to rule on.
  //
  // The vocabulary is the GRAIN'S OWN: a closed receipt renders that grain's
  // `closed` entry and a cancelled one its `cancelled`, so this adds no new
  // badge and cannot drift from the table above.
  const receiptAck = RECEIPT_STATUS_AS_ACK[line.status || ''];
  if (receiptAck && closedReceiptStatuses.includes(line.status || '') && config[receiptAck]) {
    return config[receiptAck];
  }
  if (line.on_hold) {
    const side = holdSideOf(line);
    const reason = (line.on_hold_reason_text || '').trim();
    // Nothing recorded — a hold from before any of this — is releasable by EITHER
    // side, so the badge must not name one. It said "only the customer can take it
    // off hold" over a rule that let anyone (F-H, blind stage 2, round 2).
    const who = side ? HOLD_SIDE_WORDS[side] : null;
    const rule = who ? `only ${who.sentence} can take it off hold` : 'either side can take it off hold';
    return {
      // Legacy's wording, and it names the side deliberately: the other party
      // needs to know who to chase, because they cannot lift it themselves.
      label: who ? `On Hold (${who.badge})` : 'On Hold',
      className: 'bg-orange-600 text-white',
      title: reason ? `On hold: ${reason} - ${rule}.` : `On hold - ${rule}.`,
    };
  }
  const status = line.acknowledge_status || 'new';
  return config[status] || {
    label: status,
    className: 'bg-gray-200 text-gray-900',
  };
}
